import type { ReactNode } from "react";
import type { Channel, Message } from "@breeze/shared";

const CHANNEL_STYLE: Record<Channel, { label: string; color: string }> = {
  email: { label: "EMAIL", color: "#36C5F0" },
  sms: { label: "SMS", color: "#ECB22E" },
  whatsapp: { label: "WA", color: "#2EB67D" },
  voip: { label: "CALL", color: "#A678A8" },
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diff = (now - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString();
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yest = new Date();
  yest.setDate(yest.getDate() - 1);
  const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yest)) return "Yesterday";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

type Props = {
  messages: Message[];
  loading: boolean;
};

export function Timeline({ messages, loading }: Props): JSX.Element {
  if (loading) {
    return (
      <div className="rounded-md border border-ink-700 bg-ink-900/40 px-4 py-6 text-center text-[12px] text-ink-200">
        loading messages…
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <section className="rounded-md border border-dashed border-ink-600 bg-ink-900/40 px-4 py-8 text-center">
        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-200">
          Timeline
        </div>
        <p className="mx-auto mt-2 max-w-xs text-[13px] text-ink-300">
          No messages yet on this card. Inbound email, SMS, WhatsApp and VoIP
          will show here.
        </p>
      </section>
    );
  }

  // Group by day key, preserving newest-first order.
  const groups: Array<{ key: string; items: Message[] }> = [];
  for (const m of messages) {
    const k = dayKey(m.createdAt);
    const tail = groups[groups.length - 1];
    if (tail && tail.key === k) tail.items.push(m);
    else groups.push({ key: k, items: [m] });
  }

  return (
    <div className="flex flex-col gap-4">
      {groups.map((g) => (
        <section key={g.key}>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-100">
              {g.key}
            </span>
            <span className="h-px flex-1 bg-ink-700" />
          </div>
          <ul className="flex flex-col gap-2">
            {g.items.map((m) => (
              <MessageRow key={m.msgId} message={m} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function MessageRow({ message }: { message: Message }): JSX.Element {
  const style = CHANNEL_STYLE[message.channel];
  const isOut = message.direction === "out";
  // Keep newlines in the preview so paragraph breaks survive. Prior
  // implementation collapsed all whitespace which flattened emails into
  // a single wall of text.
  const rawBody = message.bodyText ??
    (message.bodyHtml
      ? message.bodyHtml
          .replace(/<\s*br\s*\/?\s*>/gi, "\n")
          .replace(/<\/\s*(p|div|tr|li|h[1-6])\s*>/gi, "\n")
          .replace(/<[^>]+>/g, "")
          .replace(/&nbsp;/g, " ")
          .replace(/&amp;/g, "&")
          .replace(/&lt;/g, "<")
          .replace(/&gt;/g, ">")
          .replace(/&#39;|&apos;/g, "'")
          .replace(/&quot;/g, '"')
      : "");
  // Normalise but preserve newlines: collapse runs of space/tab only and
  // cap consecutive blank lines at 1.
  const preview = rawBody
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  const attachments = message.attachments ?? [];
  return (
    <li
      className={`rounded-md border border-ink-700 bg-ink-900/50 px-3 py-2.5 ${
        isOut ? "border-l-[3px] border-l-slack-aubergineLight" : ""
      }`}
    >
      <div className="flex items-center gap-2 text-[13px]">
        <span
          className="rounded-sm px-2 py-0.5 font-mono text-[12px] font-semibold tracking-[0.08em]"
          style={{ backgroundColor: `${style.color}22`, color: style.color }}
        >
          {style.label}
        </span>
        <span className="text-ink-300">
          {isOut ? (
            <OutboundAttribution message={message} />
          ) : null}
          <span className="text-ink-100">
            {isOut ? message.toAddress ?? "—" : message.fromAddress ?? "—"}
          </span>
        </span>
        <span className="ml-auto tabular-nums text-ink-200">
          {formatTime(message.createdAt)}
        </span>
        {message.status !== "sent" && message.status !== "received" ? (
          <span
            className={`text-[10px] uppercase tracking-wider ${
              message.status === "failed"
                ? "text-slack-pink"
                : "text-slack-yellow"
            }`}
          >
            {message.status}
          </span>
        ) : null}
      </div>
      {message.subject ? (
        <div className="mt-1.5 text-[12px] font-medium text-ink-100">
          {message.subject}
        </div>
      ) : null}
      {preview ? (
        <div className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-snug text-ink-200">
          {linkify(preview)}
        </div>
      ) : null}
      {attachments.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {attachments.map((a) => {
            const isImage = a.mimeType?.startsWith("image/");
            const isAudio = a.mimeType?.startsWith("audio/");
            if (isImage) {
              return (
                <div
                  key={a.attachmentId}
                  className="flex max-w-[260px] flex-col gap-1"
                >
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={a.filename}
                    className="block overflow-hidden rounded-md border border-ink-700 hover:border-ink-500"
                  >
                    <img
                      src={a.url}
                      alt={a.filename}
                      className="max-h-40 max-w-[240px] object-cover"
                      loading="lazy"
                    />
                  </a>
                  {a.description ? (
                    <div
                      className="flex items-start gap-1 rounded-md border border-ink-700 bg-ink-800/60 px-2 py-1 text-[11px] leading-snug text-ink-200"
                      title="Auto-generated by Vision tagger"
                    >
                      <span aria-hidden className="shrink-0">👁</span>
                      <span className="break-words">{a.description}</span>
                    </div>
                  ) : null}
                </div>
              );
            }
            if (isAudio) {
              return (
                <div
                  key={a.attachmentId}
                  className="flex w-full flex-col gap-1 rounded-md border border-ink-700 bg-ink-800/80 px-3 py-2"
                >
                  <div className="flex items-center gap-2 text-[11px] text-ink-300">
                    <span aria-hidden>🎙</span>
                    <span className="truncate">{a.filename}</span>
                    {a.sizeBytes ? (
                      <span className="ml-auto text-[10px] text-ink-200">
                        {formatSize(a.sizeBytes)}
                      </span>
                    ) : null}
                  </div>
                  <audio
                    controls
                    preload="none"
                    src={a.url}
                    className="w-full max-w-md"
                  />
                </div>
              );
            }
            return (
              <a
                key={a.attachmentId}
                href={a.url}
                download={a.filename}
                className="flex items-center gap-1.5 rounded-md border border-ink-700 bg-ink-800/80 px-2 py-1 text-[11px] text-ink-200 hover:border-ink-500 hover:text-ink-50"
              >
                <span aria-hidden>📎</span>
                <span className="max-w-[180px] truncate">{a.filename}</span>
                {a.sizeBytes ? (
                  <span className="text-[10px] text-ink-200">
                    {formatSize(a.sizeBytes)}
                  </span>
                ) : null}
              </a>
            );
          })}
        </div>
      ) : null}
    </li>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Outbound sender attribution. Three shapes:
//   - agent='emma_auto'  → "Emma (auto) → "
//   - agent='emma_draft' → "Emma (draft) · approved by <name> → "
//   - agent=null (human) → "You → " when the sender is the viewer, else
//     "<first name> → "
function OutboundAttribution({ message }: { message: Message }): JSX.Element {
  if (message.agent === "emma_auto") {
    return (
      <>
        <span className="font-semibold text-ink-50">Emma</span>{" "}
        <span className="text-ink-200">(auto)</span>
        <span className="text-ink-200"> → </span>
      </>
    );
  }
  if (message.agent === "emma_draft") {
    const approver = message.sentBy ? firstNameOf(message.sentBy) : "staff";
    return (
      <>
        <span className="font-semibold text-ink-50">Emma</span>{" "}
        <span className="text-ink-200">(draft)</span>
        <span className="text-ink-200"> · approved by {approver}</span>
        <span className="text-ink-200"> → </span>
      </>
    );
  }
  // Human-composed send.
  const name = message.sentBy ? firstNameOf(message.sentBy) : "You";
  return <span className="text-ink-200">{name} → </span>;
}

function firstNameOf(senderEmail: string): string {
  // sent_by is an email in most cases; pull the local-part's first segment
  // so "darren@carpetcleaning.co.uk" reads as "darren".
  const local = senderEmail.split("@")[0] ?? senderEmail;
  const first = local.split(/[._-]/)[0] ?? local;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

// Convert http(s) URLs in plain text to clickable anchors. Trailing
// punctuation is stripped so sentences like "See https://example.com." don't
// turn the full stop into part of the href.
const URL_RE = /(https?:\/\/[^\s<>"']+)/g;
function linkify(text: string): ReactNode {
  const parts = text.split(URL_RE);
  return parts.map((part, i) => {
    if (i % 2 === 1) {
      const m = part.match(/^(.*?)([.,!?;:)\]]*)$/);
      const href = m?.[1] ?? part;
      const trail = m?.[2] ?? "";
      return (
        <span key={i}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-ink-50 underline break-all hover:text-white"
          >
            {href}
          </a>
          {trail}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}
